import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { api } from "../lib/api";
import { queryClient } from "../lib/queryClient";
import { identifyUser, resetUser, capture, EVENTS } from "../lib/analytics";
import { BILLING_CHANGED } from "../lib/billingEvents";

const AuthCtx = createContext({
  user: null,
  loading: true,
  login: async () => {},
  register: async () => {},
  logout: async () => {},
  refresh: async () => {},
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      identifyUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Keep the signed-in user's plan fresh app-wide without a manual reload:
  // refresh on tab focus/visibility (e.g. returning from the Polar checkout tab)
  // and whenever a billing action broadcasts a change (checkout/cancel/resume).
  // Guarded by userRef (no /auth/me churn for logged-out visitors) and throttled
  // so focus events can't spam the endpoint.
  const userRef = useRef(user);
  userRef.current = user;
  useEffect(() => {
    let last = 0;
    const onFocus = () => {
      if (!userRef.current || document.visibilityState === "hidden") return;
      const now = Date.now();
      if (now - last < 5000) return;
      last = now;
      refresh();
    };
    const onBilling = () => {
      if (!userRef.current) return;
      last = Date.now();
      refresh();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    window.addEventListener(BILLING_CHANGED, onBilling);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
      window.removeEventListener(BILLING_CHANGED, onBilling);
    };
  }, [refresh]);

  // Capture a ?ref=CODE referral code from any entry URL and persist it so it
  // survives navigation until the visitor signs up (then register() consumes it).
  useEffect(() => {
    try {
      const ref = new URLSearchParams(window.location.search).get("ref");
      if (ref && /^[A-Za-z0-9]{4,16}$/.test(ref)) {
        localStorage.setItem("linklane_ref", ref.toUpperCase());
      }
    } catch (e) {
      /* ignore */
    }
  }, []);

  const login = async (email, password, mfaCode) => {
    queryClient.clear(); // drop any prior session's cached data
    const body = { email, password };
    if (mfaCode) body.mfa_code = mfaCode;
    const { data } = await api.post("/auth/login", body);
    setUser(data);
    identifyUser(data);
    capture(EVENTS.LOGGED_IN);
    return data;
  };

  const register = async (email, password, name, captchaToken) => {
    queryClient.clear();
    const body = { email, password, name };
    if (captchaToken) body.captcha_token = captchaToken;
    // Referral attribution: a ?ref=CODE captured on any page is stored in
    // localStorage and attached here so the referrer gets credited.
    try {
      const ref = localStorage.getItem("linklane_ref");
      if (ref) body.ref_code = ref;
    } catch (e) {
      /* localStorage unavailable — ignore */
    }
    const { data } = await api.post("/auth/register", body);
    try {
      localStorage.removeItem("linklane_ref");
    } catch (e) {
      /* ignore */
    }
    setUser(data);
    identifyUser(data);
    capture(EVENTS.SIGNED_UP);
    return data;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } finally {
      setUser(null);
      queryClient.clear(); // never leave cached user data behind
      resetUser();
    }
  };

  return (
    <AuthCtx.Provider value={{ user, loading, login, register, logout, refresh }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
